import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify';
import labelKey from "../../localization/label.json"
import { KTSVG } from '../../../_metronic/helpers';
import { Modal } from 'react-bootstrap';
import { useUpdateUserCCRequestMutation } from '../../../services/authApi';
import SearchSelect from '../../../components/textFields/SearchSelect';
import { constraintConfig } from '../../../constraintConfig';

type Props = {
    show: boolean
    handleClose: () => void
    data: any
    selectedUserIds: number[]
    selectedRows?: any[]
}

type Option = {
    value: string;
    label: string;
}

const UpdateCreditCardStatusModal = ({ show, handleClose, data, selectedUserIds, selectedRows = [] }: Props) => {
    const [mgrActionStatus, setMgrActionStatus] = useState<string | null>(null);
    const [comments, setComments] = useState<string>('');
    const [statusError, setStatusError] = useState<string>('');
    const [commentsError, setCommentsError] = useState<string>('');

    // Use the update credit card request mutation
    const [updateUserCCRequest, { isLoading, isSuccess, isError, error }] = useUpdateUserCCRequestMutation();

    // Align with EnumCreditCardCardRequest / Lst_CreditCardRequestStatus
    const baseDropdownOptions = [
        { id: String(constraintConfig.ccCardRequestStatus.RequestRejected), value: 'RequestRejected' },
        { id: String(constraintConfig.ccCardRequestStatus.CreditCardOrdered), value: 'CreditCardOrdered' }
    ];

    const dropdownOptions = baseDropdownOptions.filter(
        option => option.id !== data?.requestccCardStatus?.id?.toString()
            && option.id !== data?.creditCardCardRequest?.id?.toString()
    );

    const handleConfirmation = async () => {
        let hasError = false;

        // Validate status (required)
        if (!mgrActionStatus || mgrActionStatus === '0') {
            setStatusError('Please select a status');
            hasError = true;
            toast.error('Please select a status');
        }

        // Comments required when RequestRejected
        if (mgrActionStatus === String(constraintConfig.ccCardRequestStatus.RequestRejected) && !comments.trim()) {
            setCommentsError('Comments are required when rejecting');
            hasError = true;
            if (!statusError) toast.error('Please add comments for rejection');
        }

        if (hasError) return;

        const selectedOption = baseDropdownOptions.find(o => o.id === mgrActionStatus);
        // API expects UpdateUserCCRequestVM: OrgId, RequestccCardStatus (bool), CreditCardCardRequest { Id, Title }
        const payload = selectedUserIds.map(userId => {
            const row = selectedRows.find((r: any) => r.userAccountID === userId || r.orgUserId === userId)
                || (data?.userAccountID === userId || data?.orgUserId === userId ? data : null);
            return {
                orgId: userId,
                orgUserCreditCardRequestId: row?.orgUserCreditCardRequestId || data?.orgUserCreditCardRequestId || null,
                requestccCardStatus: true,
                creditCardCardRequest: {
                    id: parseInt(mgrActionStatus!, 10),
                    title: selectedOption?.value ?? null
                },
                comments: comments?.trim() || null
            };
        });

        await updateUserCCRequest(payload);
        handleClose();
    };

    const handleStatusChange = (e: any) => {
        if (e) {
            setMgrActionStatus(e.value);
            // Clear status error when status changes
            if (statusError) setStatusError('');
            if (e.value === String(constraintConfig.ccCardRequestStatus.RequestApproved) && commentsError) {
                setCommentsError('');
            }
        } else {
            setMgrActionStatus('0');
            setStatusError('Please select a status');
        }
    };

    const handleCommentsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setComments(e.target.value);
        // Clear error when user starts typing
        if (commentsError) setCommentsError('');
    };

    useEffect(() => {
        if (isSuccess) {
            toast.success(`Status updated successfully for ${selectedUserIds.length} user(s)`);
            handleClose();
            // Reset form
            setMgrActionStatus(null);
            setComments('');
            setStatusError('');
            setCommentsError('');
        }
    }, [isSuccess, selectedUserIds.length]);

    useEffect(() => {
        if (isError && error) {
            let errorMessage: string = 'Something went wrong';

            if ('data' in error && error.data && typeof error.data === 'object' && 'message' in error.data) {
                errorMessage = (error.data as { message: string }).message;
            }
            toast.error(errorMessage);
        }
    }, [isError, error]);

    // Reset form when modal closes
    useEffect(() => {
        if (!show) {
            setMgrActionStatus(null);
            setComments('');
            setStatusError('');
            setCommentsError('');
        }
    }, [show]);

    // Helper function to get value object safely
    const getValueObject = (): Option | null => {
        if (mgrActionStatus && mgrActionStatus !== '0') {
            const foundOption = baseDropdownOptions.find((option: any) => option.id === mgrActionStatus);
            if (foundOption && foundOption.value) {
                return {
                    value: mgrActionStatus,
                    label: foundOption.value
                };
            }
        }
        return null;
    };

    // Get options in the exact format (using filtered dropdownOptions)
    const getOptions = (): Option[] => {
        if (Array.isArray(dropdownOptions)) {
            return dropdownOptions.map((option: any) => ({
                value: option.id,
                label: option.value || '',
            }));
        }
        return [];
    };

    const isCommentsRequired = mgrActionStatus === String(constraintConfig.ccCardRequestStatus.RequestRejected);
    const isSubmitDisabled = isLoading || !mgrActionStatus || (isCommentsRequired && !comments.trim());

    return (
        <>
            <Modal
                aria-hidden='true'
                dialogClassName='modal-dialog modal-md modal-dialog-centered'
                show={show}
                onHide={handleClose}
            >
                <div className='modal-header bg-light py-4'>
                    <h2 className='mb-0'>Update Credit Card Request Status</h2>
                    {/* begin::Close */}
                    <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={handleClose}>
                        <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
                    </div>
                    {/* end::Close */}
                </div>
                <div className='modal-body'>
                    {/* Selected Users Info */}
                    <div className='col-md-12 col-lg-12 mb-4'>
                        <div className='alert alert-info d-flex align-items-center p-3'>
                            <i className='bi bi-info-circle me-2'></i>
                            <div>
                                <strong>{selectedUserIds.length} user(s)</strong> selected for status update
                            </div>
                        </div>
                    </div>

                    <div className='col-md-12 col-lg-12 mb-5'>
                        <label className='form-label fw-bold text-dark fs-6 mb-2'>
                          Request Status:
                            <span className="text-danger"> *</span>
                        </label>
                        <SearchSelect
                            options={getOptions()}
                            placeholder="Select status..."
                            value={getValueObject()}
                            onChange={handleStatusChange}
                        />
                        {statusError && (
                            <div className="text-danger mt-1 fs-7">
                                {statusError}
                            </div>
                        )}
                    </div>

                    {/* Comments Field */}
                    <div className='col-md-12 col-lg-12'>
                        <label className='form-label fw-bold text-dark fs-6 mb-2'>
                            {labelKey.comments || 'Comments'}:
                            {isCommentsRequired && <span className="text-danger"> *</span>}
                        </label>
                        <textarea
                            className={`form-control form-control-sm form-control-solid ${commentsError ? 'border-danger' : 'border-0'} p-3 resize-none min-h-100px`}
                            rows={4}
                            placeholder={isCommentsRequired ? 'Please provide comments for rejection...' : 'Add comments (optional)...'}
                            value={comments}
                            onChange={handleCommentsChange}
                            disabled={!mgrActionStatus}
                        />
                        {commentsError && (
                            <div className="text-danger mt-1 fs-7">
                                {commentsError}
                            </div>
                        )}
                        {isCommentsRequired && !commentsError && (
                            <div className="text-muted mt-1 fs-7">
                                Comments are required when rejecting
                            </div>
                        )}
                        {mgrActionStatus === String(constraintConfig.ccCardRequestStatus.RequestApproved) && (
                            <div className="text-muted mt-1 fs-7">
                                Comments are optional for approval
                            </div>
                        )}
                        {mgrActionStatus === String(constraintConfig.ccCardRequestStatus.CreditCardOrdered) && (
                            <div className="text-muted mt-1 fs-7">
                                Comments are optional for Credit Card Ordered
                            </div>
                        )}
                    </div>
                </div>
                <div className='modal-footer justify-content-center py-4'>
                    <button className='btn btn-light' onClick={handleClose}>{labelKey.cancel}</button>
                    <button
                        type='submit'
                        id='kt_sign_up_submit'
                        className='btn btn-primary'
                        onClick={handleConfirmation}
                        disabled={isSubmitDisabled}
                    >
                        {!isLoading && <span className='indicator-label'>Update</span>}
                        {isLoading && (
                            <span className='indicator-progress' style={{ display: 'block' }}>
                                Please Wait{' '}
                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                            </span>
                        )}
                    </button>
                </div>
            </Modal>
        </>
    )
}

export default UpdateCreditCardStatusModal;